/**
 * Topaz Customer Service Dashboard — Round 14 (Sep 12 2026)
 * Topaz works the inbox on its own: knowledge-base questions get a full
 * answer, anything about money / results / verification / bans / safety
 * is acknowledged and handed to a person (bell + email). This console is
 * where that person picks it up: draft, edit, send, escalate, resolve.
 * Ticket statuses are the platform's: open · in_progress · resolved · closed.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAdmin } from '../AdminApp';
import { toast } from 'sonner';
import {
  HeadphonesIcon, RefreshCw, Send, CheckCircle, ArrowUpRight, MessageSquare, Inbox, Play,
  Sparkles, UserCheck, AlertTriangle, BookOpen, XCircle,
} from 'lucide-react';

const STATUS_STYLES = {
  open:        { bg: 'bg-blue-100',    text: 'text-blue-800',    label: 'Open' },
  in_progress: { bg: 'bg-amber-100',   text: 'text-amber-800',   label: 'In Progress' },
  resolved:    { bg: 'bg-emerald-100', text: 'text-emerald-800', label: 'Resolved' },
  closed:      { bg: 'bg-gray-200',    text: 'text-gray-700',    label: 'Closed' },
};
const STATUS_ORDER = ['open', 'in_progress', 'resolved', 'closed'];

const isTopazReply = (r) => r?.admin_id === 'topaz' || String(r?.from || '').startsWith('TOPAZ');

const TopazDashboard = () => {
  const { api } = useAdmin();
  const [tickets, setTickets] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [statusCounts, setStatusCounts] = useState({});
  const [needsHumanCount, setNeedsHumanCount] = useState(0);
  const [knowledge, setKnowledge] = useState(null);
  const [filter, setFilter] = useState('');
  const [needsHumanOnly, setNeedsHumanOnly] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [draft, setDraft] = useState('');
  const [draftMeta, setDraftMeta] = useState(null);
  const [sendStatus, setSendStatus] = useState('in_progress');
  const [asTopaz, setAsTopaz] = useState(false);
  const [escalateReason, setEscalateReason] = useState('');
  const [escalatePriority, setEscalatePriority] = useState('high');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');
  const [showEscalate, setShowEscalate] = useState(false);
  const [showKnowledge, setShowKnowledge] = useState(false);
  const inFlight = useRef(false);

  const fetchAll = useCallback(async (manual = false) => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      const params = new URLSearchParams();
      if (filter) params.set('status', filter);
      if (needsHumanOnly) params.set('needs_human', 'true');
      const qs = params.toString();
      const [tRes, mRes] = await Promise.all([
        api.get(`/api/admin/workforce/topaz/tickets${qs ? `?${qs}` : ''}`),
        api.get('/api/admin/workforce/topaz/metrics'),
      ]);
      setTickets(tRes.data.tickets || []);
      setStatusCounts(tRes.data.status_counts || {});
      setNeedsHumanCount(tRes.data.needs_human_count || 0);
      setMetrics(mRes.data);
      setSelectedTicket(prev => prev ? ((tRes.data.tickets || []).find(t => t.id === prev.id) || prev) : prev);
    } catch (e) {
      if (manual) toast.error('Failed to load Topaz data');
      console.error('[Topaz] load failed', e);
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  }, [api, filter, needsHumanOnly]);

  useEffect(() => {
    fetchAll();
    const i = setInterval(() => fetchAll(false), 45000);
    return () => clearInterval(i);
  }, [fetchAll]);

  useEffect(() => {
    api.get('/api/admin/workforce/topaz/knowledge').then(r => setKnowledge(r.data)).catch(() => {});
  }, [api]);

  const processTickets = async () => {
    setActionLoading('process');
    try {
      const res = await api.post('/api/admin/workforce/topaz/process');
      if (res.data.skipped) toast.info('Topaz is already working the inbox');
      else toast.success(`Topaz: ${res.data.responded} answered, ${res.data.handed_over} handed to a person, ${res.data.open_remaining} still open`);
      fetchAll(true);
    } catch { toast.error('Processing failed'); }
    finally { setActionLoading(''); }
  };

  const generateDraft = async (ticketId) => {
    setActionLoading('draft');
    try {
      const res = await api.post(`/api/admin/workforce/topaz/tickets/${ticketId}/draft`);
      setDraft(res.data.draft);
      setDraftMeta(res.data);
      setAsTopaz(true);
    } catch { toast.error('Draft generation failed'); }
    finally { setActionLoading(''); }
  };

  const sendResponse = async () => {
    if (!selectedTicket || !draft.trim()) return;
    setActionLoading('send');
    try {
      await api.post('/api/admin/workforce/topaz/tickets/respond', {
        ticket_id: selectedTicket.id, message: draft, mark_status: sendStatus, as_topaz: asTopaz,
      });
      toast.success(`Reply sent — ticket marked ${STATUS_STYLES[sendStatus]?.label || sendStatus}`);
      setDraft(''); setDraftMeta(null); setAsTopaz(false);
      fetchAll(true);
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); }
    finally { setActionLoading(''); }
  };

  const escalateTicket = async () => {
    if (!selectedTicket || !escalateReason.trim()) return;
    setActionLoading('escalate');
    try {
      const res = await api.post('/api/admin/workforce/topaz/tickets/escalate', {
        ticket_id: selectedTicket.id, reason: escalateReason, priority: escalatePriority,
      });
      const d = res.data.delivery || {};
      toast.success(`Escalated — admins notified (${d.emailed || 0} email${d.emailed === 1 ? '' : 's'}${d.pushed ? `, ${d.pushed} push` : ''})`);
      setEscalateReason(''); setShowEscalate(false);
      fetchAll(true);
    } catch { toast.error('Escalation failed'); }
    finally { setActionLoading(''); }
  };

  const updateStatus = async (ticketId, status) => {
    try {
      await api.post(`/api/admin/workforce/topaz/tickets/${ticketId}/status?status=${status}`);
      toast.success(`Ticket → ${STATUS_STYLES[status]?.label || status}`);
      fetchAll(true);
    } catch { toast.error('Status update failed'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><RefreshCw className="w-6 h-6 animate-spin text-amber-500" /></div>;

  const activeTicket = selectedTicket;
  const isClosed = activeTicket && (activeTicket.status === 'resolved' || activeTicket.status === 'closed');
  const waitingOnPerson = activeTicket && activeTicket.needs_human && !isClosed;

  return (
    <div className="p-6 space-y-5 overflow-y-auto h-full" data-testid="topaz-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-amber-500 flex items-center justify-center">
            <HeadphonesIcon className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Topaz — Customer Support</h1>
            <p className="text-sm text-gray-500">
              Answers the knowledge base on its own · hands money, results, verification, bans and safety to a person
              {metrics?.llm_drafting ? <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 font-semibold">Claude drafting ON</span>
                : <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 font-semibold" title="Set ANTHROPIC_API_KEY on the backend to enable">template replies</span>}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowKnowledge(v => !v)} className="flex items-center gap-1.5 px-3 py-2 border rounded-lg text-sm hover:bg-gray-50" data-testid="topaz-knowledge-btn">
            <BookOpen className="w-4 h-4" /> What Topaz knows
          </button>
          <button onClick={processTickets} disabled={!!actionLoading}
            className="flex items-center gap-1.5 px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 disabled:opacity-50"
            data-testid="topaz-process-btn">
            <Play className={`w-4 h-4 ${actionLoading === 'process' ? 'animate-pulse' : ''}`} />
            {actionLoading === 'process' ? 'Working...' : 'Work the inbox now'}
          </button>
          <button onClick={() => fetchAll(true)} className="flex items-center gap-1.5 px-3 py-2 border rounded-lg text-sm hover:bg-gray-50">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </div>

      {showKnowledge && knowledge && (
        <div className="bg-white border rounded-xl p-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-xs" data-testid="topaz-knowledge">
          <div>
            <h3 className="font-semibold text-gray-700 mb-2">Answers on its own</h3>
            <div className="flex flex-wrap gap-1.5">
              {(knowledge.knowledge_base || []).map(k => <span key={k.key} className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">{k.label}</span>)}
            </div>
          </div>
          <div>
            <h3 className="font-semibold text-gray-700 mb-2">Always hands to a person</h3>
            <div className="flex flex-wrap gap-1.5">
              {(knowledge.escalation_rules || []).map(r => (
                <span key={r.key} className={`px-2 py-0.5 rounded-full ${r.priority === 'critical' ? 'bg-red-100 text-red-700' : r.priority === 'high' ? 'bg-orange-100 text-orange-700' : 'bg-amber-50 text-amber-700'}`}>
                  {r.label}
                </span>
              ))}
            </div>
            <p className="text-gray-500 mt-2">Critical → bell + email + push · High → bell + email · Medium → bell. Hand-overs older than 24h are chased again.</p>
          </div>
        </div>
      )}

      {/* Metrics Bar */}
      {metrics && (
        <div className="grid grid-cols-2 md:grid-cols-5 lg:grid-cols-9 gap-2">
          {[
            { label: 'Total', val: metrics.total_tickets, color: 'text-gray-700' },
            { label: 'Open', val: metrics.open, color: 'text-blue-600' },
            { label: 'In Progress', val: metrics.in_progress, color: 'text-amber-600' },
            { label: 'Needs a person', val: metrics.needs_human, color: metrics.needs_human > 0 ? 'text-amber-700' : 'text-gray-600' },
            { label: 'SLA Breached', val: metrics.sla_breached, color: metrics.sla_breached > 0 ? 'text-red-600' : 'text-gray-600' },
            { label: 'Answered 24h', val: metrics.answered_24h, color: 'text-emerald-600' },
            { label: 'Handed over 24h', val: metrics.handed_over_24h, color: 'text-orange-600' },
            { label: 'Avg Resolve', val: `${metrics.avg_resolution_hours}h`, color: 'text-cyan-600' },
            { label: 'Resolve Rate', val: `${metrics.resolution_rate}%`, color: 'text-violet-600' },
          ].map(m => (
            <div key={m.label} className="bg-white border rounded-lg p-3 text-center">
              <p className={`text-lg font-bold ${m.color}`}>{m.val ?? 0}</p>
              <p className="text-[10px] text-gray-500">{m.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Status Filter Tabs */}
      <div className="flex gap-1 flex-wrap items-center">
        <button onClick={() => setFilter('')}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${!filter ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          data-testid="filter-all">
          All
        </button>
        {STATUS_ORDER.map(status => {
          const s = STATUS_STYLES[status];
          return (
            <button key={status} onClick={() => setFilter(filter === status ? '' : status)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${filter === status ? 'bg-gray-800 text-white' : `${s.bg} ${s.text} hover:opacity-80`}`}
              data-testid={`filter-${status}`}>
              {s.label} ({statusCounts[status] || 0})
            </button>
          );
        })}
        <button onClick={() => setNeedsHumanOnly(v => !v)}
          className={`ml-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition flex items-center gap-1 ${needsHumanOnly ? 'bg-amber-600 text-white' : 'bg-amber-50 text-amber-800 hover:bg-amber-100'}`}
          data-testid="filter-needs-human">
          <UserCheck className="w-3.5 h-3.5" /> Needs a person ({needsHumanCount})
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Ticket List */}
        <div className="bg-white border rounded-xl overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
              <Inbox className="w-4 h-4" /> Tickets Queue
            </h2>
            <span className="text-xs text-gray-500">{tickets.length} shown</span>
          </div>
          <div className="divide-y max-h-[560px] overflow-y-auto">
            {tickets.length === 0 ? (
              <div className="p-8 text-center text-gray-400 text-sm">No tickets found</div>
            ) : tickets.map(t => {
              const s = STATUS_STYLES[t.status] || STATUS_STYLES.open;
              const open = t.status === 'open' || t.status === 'in_progress';
              return (
                <div key={t.id}
                  className={`px-4 py-3 cursor-pointer hover:bg-gray-50 transition ${selectedTicket?.id === t.id ? 'bg-amber-50 border-l-4 border-amber-500' : ''}`}
                  onClick={() => { setSelectedTicket(t); setDraft(''); setDraftMeta(null); setShowEscalate(false); setAsTopaz(false); }}
                  data-testid={`ticket-row-${t.id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-mono text-gray-500">{t.id}</span>
                    <div className="flex items-center gap-1">
                      {t.needs_human && open && <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-amber-100 text-amber-800">Needs a person</span>}
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${s.bg} ${s.text}`}>{s.label}</span>
                    </div>
                  </div>
                  <p className="text-sm font-medium text-gray-900 truncate">{t.subject}</p>
                  <div className="flex items-center gap-2 mt-1 text-[10px] text-gray-500 flex-wrap">
                    <span>{t.username}</span>
                    <span>{t.category}</span>
                    {t.topaz_topic && <span className="text-violet-600">{t.topaz_topic}</span>}
                    {t.sla_breached && <span className="text-red-600 font-bold">SLA BREACHED</span>}
                    {t.topaz_responded && !t.human_responded && <span className="text-amber-600">Topaz replied</span>}
                    {t.human_responded && <span className="text-emerald-600">Person replied</span>}
                    <span className="ml-auto">{t.sla_hours_elapsed}h ago</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Ticket Detail + Actions */}
        <div className="bg-white border rounded-xl overflow-hidden">
          {!activeTicket ? (
            <div className="p-12 text-center text-gray-400">
              <MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm">Select a ticket to view the thread & reply</p>
            </div>
          ) : (
            <div className="flex flex-col h-full max-h-[680px]">
              {/* Ticket Header */}
              <div className="px-4 py-3 bg-gray-50 border-b">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-mono text-gray-500">{activeTicket.id}</span>
                  <div className="flex items-center gap-1.5">
                    {activeTicket.status === 'open' && (
                      <button onClick={() => updateStatus(activeTicket.id, 'in_progress')}
                        className="text-[10px] px-2 py-0.5 bg-amber-100 text-amber-700 rounded hover:bg-amber-200"
                        data-testid="mark-in-progress">Mark In Progress</button>
                    )}
                    {!isClosed && (
                      <button onClick={() => updateStatus(activeTicket.id, 'resolved')}
                        className="text-[10px] px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded hover:bg-emerald-200"
                        data-testid="mark-resolved">Mark Resolved</button>
                    )}
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${(STATUS_STYLES[activeTicket.status] || STATUS_STYLES.open).bg} ${(STATUS_STYLES[activeTicket.status] || STATUS_STYLES.open).text}`}>
                      {(STATUS_STYLES[activeTicket.status] || STATUS_STYLES.open).label}
                    </span>
                  </div>
                </div>
                <h3 className="font-semibold text-gray-900">{activeTicket.subject}</h3>
                <p className="text-xs text-gray-500 mt-0.5">From: {activeTicket.username} {activeTicket.email ? `· ${activeTicket.email}` : ''} ({activeTicket.category})</p>
              </div>

              {waitingOnPerson && (
                <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 text-xs text-amber-900 flex items-start gap-2" data-testid="needs-human-banner">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <div>
                    <span className="font-semibold">Waiting on a person.</span> {activeTicket.escalation_reason || 'Needs human verification.'}
                    {activeTicket.escalation_priority && <span className="ml-1 uppercase text-[10px] font-bold">({activeTicket.escalation_priority})</span>}
                    <div className="text-amber-700 mt-0.5">Your reply below goes to the member and clears the hand-off.</div>
                  </div>
                </div>
              )}

              {/* Message Thread */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-gray-700">{activeTicket.username}</span>
                    <span className="text-[10px] text-gray-400">{activeTicket.created_at ? new Date(activeTicket.created_at).toLocaleString() : ''}</span>
                  </div>
                  <p className="text-sm text-gray-800 whitespace-pre-line">{activeTicket.message}</p>
                </div>
                {(activeTicket.responses || []).map((r, i) => {
                  const topaz = isTopazReply(r);
                  return (
                    <div key={i} className={`rounded-lg p-3 ${topaz ? 'bg-amber-50 border border-amber-200' : 'bg-blue-50 border border-blue-100'}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium" style={{ color: topaz ? '#d97706' : '#2563eb' }}>
                          {r.admin_name || r.from || 'Support'}
                          {topaz && <span className="ml-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">AI</span>}
                          {r.source && <span className="ml-1.5 text-[10px] text-gray-400">{r.source}</span>}
                        </span>
                        <span className="text-[10px] text-gray-400">{r.timestamp ? new Date(r.timestamp).toLocaleString() : ''}</span>
                      </div>
                      <p className="text-sm text-gray-800 whitespace-pre-line">{r.response ?? r.message}</p>
                    </div>
                  );
                })}
              </div>

              {/* Action Panel */}
              {!isClosed ? (
                <div className="border-t p-4 space-y-3 bg-gray-50">
                  {!showEscalate ? (
                    <>
                      <div className="flex items-center gap-2 flex-wrap">
                        <button onClick={() => generateDraft(activeTicket.id)} disabled={!!actionLoading}
                          className="text-xs px-3 py-1.5 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 font-medium flex items-center gap-1"
                          data-testid="generate-draft-btn">
                          <Sparkles className="w-3 h-3" /> {actionLoading === 'draft' ? 'Drafting...' : 'Draft with Topaz'}
                        </button>
                        {draftMeta && (
                          <span className="text-[10px] text-gray-500">
                            {draftMeta.source} · {draftMeta.matched_topic} · {draftMeta.confidence}
                            {draftMeta.needs_human && <span className="ml-1 text-amber-700 font-semibold">· Topaz would hand this to a person: {draftMeta.reason}</span>}
                          </span>
                        )}
                      </div>
                      <textarea value={draft} onChange={e => setDraft(e.target.value)}
                        className="w-full border rounded-lg px-3 py-2 text-sm min-h-[110px] resize-y"
                        placeholder="Type a reply or draft one with Topaz..."
                        data-testid="response-textarea" />
                      <div className="flex items-center gap-2 flex-wrap">
                        <select value={sendStatus} onChange={e => setSendStatus(e.target.value)}
                          className="text-xs border rounded-lg px-2 py-1.5 bg-white"
                          data-testid="send-status-select">
                          <option value="in_progress">Keep In Progress</option>
                          <option value="resolved">Mark Resolved</option>
                          <option value="closed">Mark Closed</option>
                        </select>
                        <label className="text-[11px] text-gray-600 flex items-center gap-1">
                          <input type="checkbox" checked={asTopaz} onChange={e => setAsTopaz(e.target.checked)} data-testid="sign-as-topaz" /> Sign as Topaz
                        </label>
                        <button onClick={sendResponse} disabled={!draft.trim() || !!actionLoading}
                          className="flex items-center gap-1.5 px-4 py-1.5 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 disabled:opacity-50"
                          data-testid="send-response-btn">
                          <Send className="w-3.5 h-3.5" /> {actionLoading === 'send' ? 'Sending...' : 'Send Reply'}
                        </button>
                        <button onClick={() => setShowEscalate(true)}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-xs font-medium hover:bg-red-200 ml-auto"
                          data-testid="escalate-btn">
                          <ArrowUpRight className="w-3 h-3" /> Escalate to admins
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="text-xs font-semibold text-red-700">Escalate — rings the admin bell, emails admins (high/critical), pushes (critical)</p>
                      <textarea value={escalateReason} onChange={e => setEscalateReason(e.target.value)}
                        className="w-full border border-red-200 rounded-lg px-3 py-2 text-sm min-h-[60px] resize-none"
                        placeholder="Why is this being escalated?"
                        data-testid="escalate-reason" />
                      <div className="flex gap-2 items-center">
                        <select value={escalatePriority} onChange={e => setEscalatePriority(e.target.value)} className="text-xs border rounded-lg px-2 py-1.5 bg-white" data-testid="escalate-priority">
                          <option value="critical">Critical</option>
                          <option value="high">High</option>
                          <option value="medium">Medium</option>
                        </select>
                        <button onClick={escalateTicket} disabled={!escalateReason.trim() || !!actionLoading}
                          className="flex items-center gap-1.5 px-4 py-1.5 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50"
                          data-testid="confirm-escalate-btn">
                          <ArrowUpRight className="w-3.5 h-3.5" /> {actionLoading === 'escalate' ? 'Escalating...' : 'Confirm Escalation'}
                        </button>
                        <button onClick={() => setShowEscalate(false)}
                          className="px-3 py-1.5 text-sm text-gray-600 border rounded-lg hover:bg-gray-100">Cancel</button>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className={`border-t p-4 text-center text-sm ${activeTicket.status === 'resolved' ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-700'}`}>
                  <span className="flex items-center justify-center gap-1.5">
                    {activeTicket.status === 'resolved' ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                    Ticket {activeTicket.status} {activeTicket.resolved_by === 'topaz' ? 'by Topaz' : ''}{activeTicket.resolution ? ` — ${activeTicket.resolution}` : ''}
                  </span>
                  <button onClick={() => updateStatus(activeTicket.id, 'in_progress')} className="mt-2 text-xs underline text-gray-500" data-testid="reopen-ticket">Reopen</button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TopazDashboard;
